// Kebab (⋯) action menu. Ports the pbx-portal ActionMenu component.
//
// Props:
//   items: [{ label, onClick, disabled?, danger?, divider? }]
//   label: alternate trigger text (default: '⋯' glyph)
//   title: accessible label + tooltip for the trigger (default 'Actions').
//          Pass something specific per row (e.g. `Actions for ${name}`) so
//          screen readers announce which row's menu is opening.
//
// Items rendered top-to-bottom in the given order. `divider: true`
// items render as a horizontal rule. Danger items get red text.
//
// Closes on: outside click, Escape, or selecting an item.

import { useEffect, useRef, useState } from 'react';

export default function ActionMenu({ items, label = null, title = 'Actions', className = '' }) {
  const [open, setOpen] = useState(false);
  // Drop direction: menus near the bottom of the viewport open upward so the
  // last rows of long tables stay fully clickable.
  const [dropUp, setDropUp] = useState(false);
  const ref = useRef(null);

  const toggle = (e) => {
    e.stopPropagation();
    if (!open) {
      const rect = ref.current?.getBoundingClientRect();
      // ~est. menu height incl. dividers; flip when it wouldn't fit below.
      setDropUp(rect ? window.innerHeight - rect.bottom < 240 : false);
    }
    setOpen((v) => !v);
  };

  useEffect(() => {
    if (!open) return undefined;
    function onDown(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    function onKey(e)  { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const visibleItems = (items || []).filter((it) => it && (it.divider || (it.label && (it.onClick || it.href))));
  if (visibleItems.length === 0) return null;

  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={toggle}
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={title}
        title={title}
      >
        {label ?? (
          <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden="true">
            <path d="M10 3.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM10 8.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM10 13.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" />
          </svg>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute right-0 z-20 min-w-[10rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg ${
            dropUp ? 'bottom-full mb-1' : 'mt-1'
          }`}
        >
          {visibleItems.map((item, i) => {
            if (item.divider) {
              return <div key={`div-${i}`} className="my-1 h-px bg-gray-100" />;
            }
            const cls = item.danger
              ? 'text-red-600 hover:bg-red-50 disabled:text-red-300'
              : 'text-gray-700 hover:bg-gray-50 disabled:text-gray-300';
            return (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  if (!item.disabled) item.onClick?.();
                }}
                title={item.title || undefined}
                className={`block w-full px-3 py-1.5 text-left text-sm ${cls} disabled:cursor-not-allowed`}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
